// apps/nos/client/src/hooks/useVertexRecommendations.ts
import { useQuery } from "@tanstack/react-query";
import {
  VertexRecommendationsApiError,
  fetchVertexRecommendations,
  type VertexRecommendationsRequest,
  type VertexRecommendationsResponse,
} from "@/services/vertexRecommendations_api";

type Params = {
  flavorId: string | null;
  formulaId: number | null;
  versionId: number | null;
  request: VertexRecommendationsRequest | null;
  previewId?: string | null;
};

// 422 codes the server emits when the request is structurally fine but the
// inputs make recommendations impossible. The UI renders an empty state
// rather than treating these as errors.
const EXPECTED_CODES = new Set(["no_target", "no_formulation"]);

export function useVertexRecommendations({
  flavorId,
  formulaId,
  versionId,
  request,
  previewId = null,
}: Params) {
  return useQuery<VertexRecommendationsResponse, VertexRecommendationsApiError>(
    {
      // Include the request payload in the key so different vertex
      // selections don't share a cache slot. JSON.stringify is safe — the
      // request shape is a small flat object with arrays of numbers.
      queryKey: ["vertex-recommendations", versionId, previewId, request],
      queryFn: ({ signal }) =>
        fetchVertexRecommendations(
          flavorId!,
          formulaId!,
          versionId!,
          request!,
          signal,
          previewId,
        ),
      enabled:
        flavorId != null &&
        formulaId != null &&
        versionId != null &&
        request != null,
      // Vertex recommendations depend on (a) the saved formulation (which
      // bumps versionId) and (b) the active pin. Pin changes invalidate
      // the parent viz cache; reuse staleTime: Infinity to avoid duplicate
      // fetches when the user briefly tabs through vertices.
      staleTime: Infinity,
      retry: (failureCount, error) => {
        if (error instanceof VertexRecommendationsApiError) {
          // Don't retry expected 422s or any 4xx — they're not transient.
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
    },
  );
}

export { EXPECTED_CODES as VERTEX_REC_EXPECTED_CODES };
